<template>
  <div v-if="visible" class="signature-mask">
    <div class="signature-container">
      <div class="signature-header">
        <span class="signature-title">请签名</span>
        <van-icon name="cross" class="signature-close" @click="handleClose" />
      </div>
      <div class="signature-error" v-if="errorMessage">
        {{ errorMessage }}
      </div>
      <div class="signature-content">
        <vue-signature-pad
          ref="signaturePad"
          width="100%"
          height="200px"
          :options="signatureOptions"
          class="signature-pad"
        />
      </div>
      <div class="signature-footer">
        <van-button class="signature-btn signature-btn-clear" @click="handleClear">重写</van-button>
        <van-button class="signature-btn signature-btn-confirm" @click="handleConfirm">确定</van-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SignaturePad',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    signatureOptions: {
      type: Object,
      default: () => ({
        penColor: '#000000',
        backgroundColor: '#f5f5f5'
      })
    }
  },
  data() {
    return {
      errorMessage: ''
    };
  },
  watch: {
    visible(newVal) {
      // 当弹窗关闭时，清除错误提示
      if (!newVal) {
        this.errorMessage = '';
      }
    }
  },
  methods: {
    showError(message) {
      this.errorMessage = message;
      // 3秒后自动清除错误提示
      setTimeout(() => {
        this.errorMessage = '';
      }, 3000);
    },
    handleClear() {
      this.clearSignature();
      this.errorMessage = '';
    },
    handleConfirm() {
      this.confirmSignature();
    },
    handleClose() {
      this.$emit('close');
    },
    clearSignature() {
      if (!this.$refs.signaturePad) {
        return;
      }
      
      // 尝试多种清除方法
      const signaturePad = this.$refs.signaturePad;
      if (typeof signaturePad.clear === 'function') {
        signaturePad.clear();
      } else if (typeof signaturePad.clearSignature === 'function') {
        signaturePad.clearSignature();
      } else if (typeof signaturePad.reset === 'function') {
        signaturePad.reset();
      } else if (signaturePad.$el) {
        const canvas = signaturePad.$el.querySelector('canvas');
        if (canvas) {
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }
    },
    // 检查签名画布是否为空
    isEmptySignature(canvas) {
      if (!canvas) return true;
      
      try {
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // 检查所有像素点，如果全部都是透明或背景色，说明是空签名
        // 由于背景色是灰色（#f5f5f5，rgb(245, 245, 245)），我们需要检查是否有非灰色像素
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          
          // 如果有非透明且与背景色差异较大的像素（签名一般是黑色或深色），说明有签名内容
          // 背景色是 #f5f5f5 (245, 245, 245)，签名通常是黑色或深色
          if (a > 0) {
            // 检查是否与背景色差异较大（差值大于10）
            const diff = Math.abs(r - 245) + Math.abs(g - 245) + Math.abs(b - 245);
            if (diff > 30) {
              return false; // 有签名内容
            }
          }
        }
        
        return true;
      } catch (error) {
        console.error('检查签名是否为空失败:', error);
        return true; // 出错时认为为空，需要重新签名
      }
    },
    confirmSignature() {
      if (!this.$refs.signaturePad) {
        this.showError('签名组件未初始化');
        return;
      }

      // 将签名转换为图片
      let dataURL = '';
      let canvas = null;
      try {
        // 优先尝试从 canvas 元素直接获取（最可靠的方式）
        const signaturePadRef = this.$refs.signaturePad;
        
        // 尝试多种方式获取 canvas 元素
        if (signaturePadRef) {
          // 方式1: 从 $el 获取
          if (signaturePadRef.$el) {
            canvas = signaturePadRef.$el.querySelector('canvas') || signaturePadRef.$el;
          }
          // 方式2: 直接是 canvas 元素
          else if (signaturePadRef.tagName === 'CANVAS') {
            canvas = signaturePadRef;
          }
          // 方式3: 从组件实例获取 canvas
          else if (signaturePadRef.$canvas) {
            canvas = signaturePadRef.$canvas;
          }
          // 方式4: 尝试组件的方法
          else if (typeof signaturePadRef.getCanvas === 'function') {
            canvas = signaturePadRef.getCanvas();
          }
          
          // 方式5: 尝试使用组件的 isEmpty 方法（如果可用）
          if (typeof signaturePadRef.isEmpty === 'function') {
            if (signaturePadRef.isEmpty()) {
              this.showError('请先进行签名');
              return;
            }
          }
        }
        
        // 如果找到了 canvas，检查是否为空
        if (canvas) {
          // 检查签名是否为空
          if (this.isEmptySignature(canvas)) {
            this.showError('请先进行签名');
            return;
          }
          
          // 获取 dataURL
          if (canvas.toDataURL) {
            dataURL = canvas.toDataURL('image/png');
          }
        }
        
        // 如果没找到 canvas 或 dataURL 为空，尝试使用组件的方法
        if (!dataURL && signaturePadRef) {
          if (typeof signaturePadRef.save === 'function') {
            const result = signaturePadRef.save();
            if (result && typeof result === 'object' && result.dataURL) {
              dataURL = result.dataURL;
            } else if (typeof result === 'string') {
              dataURL = result;
            }
          } else if (typeof signaturePadRef.saveSignature === 'function') {
            const result = signaturePadRef.saveSignature();
            if (result && typeof result === 'object' && result.dataURL) {
              dataURL = result.dataURL;
            } else if (typeof result === 'string') {
              dataURL = result;
            }
          } else if (typeof signaturePadRef.toDataURL === 'function') {
            dataURL = signaturePadRef.toDataURL();
          }
        }
        
        // 确保 dataURL 是字符串类型
        if (!dataURL || typeof dataURL !== 'string') {
          console.error('无法获取签名数据，dataURL 类型:', typeof dataURL, '值:', dataURL);
          this.showError('获取签名失败，请重试');
          return;
        }
        
        // 保存签名图片
        this.saveSignatureImage(dataURL);
      } catch (error) {
        console.error('获取签名失败:', error);
        this.showError('获取签名失败: ' + error.message);
      }
    },
    saveSignatureImage(dataURL) {
      // 确保 dataURL 是字符串
      if (typeof dataURL !== 'string') {
        console.error('dataURL 必须是字符串类型:', typeof dataURL);
        this.showError('签名数据格式错误');
        return;
      }
      
      try {
        // 检查 dataURL 格式，如果已经是完整的 base64 字符串（包含 data:image 前缀），直接使用
        // 如果没有前缀，可能是纯 base64 字符串，需要添加前缀
        let finalDataURL = dataURL;
        
        // 如果 dataURL 包含逗号，说明是完整的 data:image/png;base64,xxx 格式
        if (dataURL.includes(',')) {
          finalDataURL = dataURL;
        } else {
          // 如果不包含逗号，可能是纯 base64 字符串，添加前缀
          finalDataURL = 'data:image/png;base64,' + dataURL;
        }
        
        // 触发确认事件，传递签名数据
        this.$emit('confirm', finalDataURL);
      } catch (error) {
        console.error('保存签名失败:', error);
        this.showError('保存签名失败: ' + error.message);
      }
    }
  }
};
</script>

<style scoped>
/* 签名弹窗样式 */
.signature-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.signature-container {
  width: 90%;
  max-width: 500px;
  background-color: #ffffff;
  border-radius: 0.5rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.signature-header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #eee;
  position: relative;
}

.signature-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #333;
  text-align: center;
}

.signature-close {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.2rem;
  color: #999;
  cursor: pointer;
  transition: color 0.3s;
}

.signature-close:hover {
  color: #333;
}

.signature-close:active {
  color: #666;
}

.signature-error {
  padding: 0.5rem 1rem;
  background-color: #fff3cd;
  color: #856404;
  font-size: 0.9rem;
  text-align: center;
  border-bottom: 1px solid #ffeeba;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.signature-content {
  padding: 1rem;
  background-color: #f8f8f8;
}

.signature-pad {
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  background-color: #f5f5f5;
}

.signature-footer {
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  gap: 1rem;
}

.signature-btn {
  flex: 1;
  height: 2.5rem;
  border-radius: 0.25rem;
  font-size: 1rem;
}

.signature-btn-clear {
  background-color: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
}

.signature-btn-confirm {
  background-color: #d32f2f;
  color: #ffffff;
  border: none;
}
</style>

